/**
 * Meta Pixel and web analytics.
 *
 * ## Why this exists
 *
 * Until now the site measured nothing. There was a setting called
 * `integrations.analytics_id` in the back office and **nothing in the application read
 * it** — an editor could type a measurement ID into the box and the only effect was
 * that the box had text in it. For a site with ten products and a working cart, that
 * meant no retargeting, no conversion optimisation, and no way to tell which ad
 * produced which order.
 *
 * ## Shape
 *
 * Both tags are driven by SETTINGS rather than baked into the bundle, matching the rest
 * of the site: an ID goes in the back office, and an empty ID loads nothing at all. No
 * third-party script reaches a visitor who has not been asked for.
 *
 * ## Three things that would break it
 *
 * 1. **The build renders every route.** `entry-server.tsx` calls `renderToString`, so
 *    module scope runs in Node. Nothing here touches `window` or `document` outside a
 *    function body, and every entry point returns early on the server. A bare
 *    `document.createElement` at module scope would fail the build, not the browser.
 * 2. **Events can fire before the script arrives.** Meta's snippet installs `fbq` as a
 *    queue synchronously and the real library drains it on load, so calling `fbq` a
 *    millisecond after injection is safe and is how the official snippet behaves. The
 *    same is true of `gtag` and its `dataLayer`. Do not "fix" this by waiting for
 *    `onload` — the AddToCart that happens during loading would be the one lost.
 * 3. **A single-page app fires PageView once.** The base code counts the first load;
 *    every route change after that is invisible unless it is sent by hand. `useTracking`
 *    does that, and deliberately skips the first render so the initial view is not
 *    counted twice.
 */

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

type FbqArgs = [command: string, ...rest: unknown[]];

interface FbqFn {
  (...args: FbqArgs): void;
  callMethod?: (...args: FbqArgs) => void;
  queue?: FbqArgs[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
}

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** One line of a Meta commerce event. */
export interface TrackedItem {
  productId: number;
  name: string;
  quantity: number;
  /** The decimal string the API returns, e.g. `"11990.00"`. */
  unitPrice: string;
}

/* -------------------------------------------------------------------------- */
/* Loading                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Whether each tag has been injected.
 *
 * Module scope, not React state: `SiteLayout` remounts on some navigations and
 * StrictMode runs effects twice in development. Either would otherwise inject the
 * snippet a second time, and two copies of the Pixel double-count every event — which
 * looks like the ads working twice as well as they do.
 */
let pixelLoaded = false;
let analyticsLoaded = false;

/** The configured pixel, remembered so the event helpers can no-op without it. */
let pixelId: string | null = null;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * Install Meta's base code.
 *
 * This is the official snippet, written out rather than `eval`'d from a string so that
 * it type-checks and so a reader can see what it does. The stub it installs pushes into
 * `fbq.queue`; `fbevents.js` replaces `callMethod` on load and drains what it finds.
 */
function loadPixel(id: string): void {
  if (pixelLoaded || !isBrowser()) return;
  pixelLoaded = true;
  pixelId = id;

  if (!window.fbq) {
    const fbq: FbqFn = function (...args: FbqArgs) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue?.push(args);
    } as FbqFn;

    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.push = fbq;

    window.fbq = fbq;
    window._fbq ??= fbq;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  window.fbq?.('init', id);
  window.fbq?.('track', 'PageView');
}

/**
 * Install Google's tag.
 *
 * Accepts whatever the setting holds — a GA4 `G-…` measurement ID or a `GT-…` tag ID —
 * because `gtag.js` resolves either from the same endpoint and the back office asks for
 * "measurement ID" without dictating which.
 */
function loadAnalytics(id: string): void {
  if (analyticsLoaded || !isBrowser()) return;
  analyticsLoaded = true;

  window.dataLayer ??= [];
  // Must be a real `function` — gtag forwards `arguments`, so an arrow that captures
  // nothing would send an empty payload.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', id);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}

/* -------------------------------------------------------------------------- */
/* Events                                                                      */
/* -------------------------------------------------------------------------- */

/** Send a standard Meta event, or do nothing when no pixel is configured. */
function track(event: string, params?: Record<string, unknown>): void {
  if (!isBrowser() || !pixelId) return;
  window.fbq?.('track', event, params);
}

/** Total of a set of lines, as the number Meta's `value` expects. */
function valueOf(items: TrackedItem[]): number {
  const total = items.reduce((sum, item) => sum + Number(item.unitPrice) * item.quantity, 0);
  // Guard against a malformed price string turning the whole event into NaN, which Meta
  // rejects silently — the event vanishes and nothing says why.
  return Number.isFinite(total) ? Math.round(total * 100) / 100 : 0;
}

/**
 * `content_ids` must match the product catalogue's own ids.
 *
 * The database primary key is used here. When the Commerce Manager catalogue is built,
 * its feed has to publish the same value as each item's `id`, or Meta cannot join an
 * event to a product and dynamic product ads have nothing to show.
 */
const idsOf = (items: TrackedItem[]) => items.map((item) => String(item.productId));

const contentsOf = (items: TrackedItem[]) =>
  items.map((item) => ({
    id: String(item.productId),
    quantity: item.quantity,
    item_price: Number(item.unitPrice),
  }));

export function trackPageView(): void {
  track('PageView');
}

export function trackViewContent(item: TrackedItem, currency: string): void {
  track('ViewContent', {
    content_type: 'product',
    content_ids: idsOf([item]),
    content_name: item.name,
    value: valueOf([item]),
    currency,
  });
}

export function trackAddToCart(item: TrackedItem, currency: string): void {
  track('AddToCart', {
    content_type: 'product',
    content_ids: idsOf([item]),
    content_name: item.name,
    contents: contentsOf([item]),
    value: valueOf([item]),
    currency,
  });
}

export function trackInitiateCheckout(items: TrackedItem[], currency: string): void {
  track('InitiateCheckout', {
    content_type: 'product',
    content_ids: idsOf(items),
    contents: contentsOf(items),
    num_items: items.reduce((n, item) => n + item.quantity, 0),
    value: valueOf(items),
    currency,
  });
}

/**
 * A completed order.
 *
 * `value` is the SERVER's total, not the cart's — the server re-prices every line at
 * checkout, so the basket snapshot in `localStorage` can be out of date, and reporting
 * revenue the business did not take would quietly corrupt every return-on-spend figure
 * built on top of it.
 */
export function trackPurchase(args: {
  orderNumber: string;
  total: string;
  currency: string;
  items: TrackedItem[];
}): void {
  const total = Number(args.total);
  track('Purchase', {
    content_type: 'product',
    content_ids: idsOf(args.items),
    contents: contentsOf(args.items),
    num_items: args.items.reduce((n, item) => n + item.quantity, 0),
    value: Number.isFinite(total) ? total : valueOf(args.items),
    currency: args.currency,
    order_id: args.orderNumber,
  });
}

/** An enquiry, a quote request or a counter configuration — anything a human answers. */
export function trackLead(source: string): void {
  track('Lead', { content_category: source });
}

/* -------------------------------------------------------------------------- */
/* Wiring                                                                      */
/* -------------------------------------------------------------------------- */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { setting, useBootstrap, type BootstrapData } from './queries.js';

/**
 * Read a setting as a trimmed string, whatever the database actually holds.
 *
 * NOT defensive programming for its own sake — this took the live site down on
 * 2026-09-29. `site_settings.value` is `jsonb`, so a pixel ID written as
 * `1461126889406918` rather than `"1461126889406918"` comes back a NUMBER, and
 * `.trim()` on a number throws. The throw happened inside `useTracking`, which is
 * called by `PublicSite` above the layout and outside every error boundary, so React
 * tore down the whole tree and every visitor got a blank page.
 *
 * The lesson is the general one: a value that arrives from the database is not the type
 * the catalogue says it is, and a setting must never be able to take the site down.
 * Coerce here, where it is read, rather than trusting whatever wrote it.
 */
function textSetting(data: BootstrapData | undefined, key: string): string {
  const value = setting<unknown>(data, key, '');
  if (typeof value === 'string') return value.trim();
  // A bare number in the JSON — the case that broke it. Rendered without exponent or
  // separators, which is what a pixel ID needs.
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  // Anything else (null, boolean, object) is not an ID, and loading a tag with it would
  // put a junk request on every page. Treated as "not configured".
  return '';
}

/**
 * Load the configured tags and report route changes.
 *
 * Called once, from the public site's layout. The back office is deliberately NOT
 * tracked: staff clicking through the admin all day would swamp the numbers the ads are
 * judged on, and their behaviour is nobody's business.
 */
export function useTracking(): void {
  const { data } = useBootstrap();
  const { pathname } = useLocation();

  const configuredPixel = textSetting(data, 'integrations.meta_pixel_id');
  const configuredAnalytics = textSetting(data, 'integrations.analytics_id');

  useEffect(() => {
    if (configuredPixel) loadPixel(configuredPixel);
  }, [configuredPixel]);

  useEffect(() => {
    if (configuredAnalytics) loadAnalytics(configuredAnalytics);
  }, [configuredAnalytics]);

  /**
   * Skip the first run.
   *
   * `loadPixel` already sent a PageView for the page that loaded. Sending another here
   * would double every landing, and since landings are the denominator of most of the
   * rates in Ads Manager, it would halve them all.
   */
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    trackPageView();
    // GA4 reports SPA navigations by itself once configured, so only Meta needs telling.
  }, [pathname]);
}
